/* Manchester · Quickview drawer logic.
   See snippets/manchester-quickview.liquid for markup + styles.
   Triggered by .mfg-quickview-trigger click events (delegated).

   Flow: click -> open drawer with skeleton -> fetch /products/HANDLE.js
   -> render minimal product card -> let user pick variant -> ATC -> on
   success close drawer + open cart drawer. */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-mfg-quickview]');
  if (!drawer) return;

  var bodyEl = drawer.querySelector('[data-mfg-qv-body]');
  // Template is a sibling of the drawer in the snippet, so search the whole
  // document (not just inside drawer) — previous version returned null here,
  // which made the IIFE early-return and skip the click listener registration.
  var tpl = document.querySelector('[data-mfg-qv-template]');
  if (!bodyEl || !tpl) return;

  var lastFocused = null;
  var currentProduct = null;
  var selectedOptions = {};
  var inFlight = null;

  function formatMoney(cents) {
    var n = (cents / 100).toLocaleString('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return '$' + n + ' MXN';
  }

  function showSkeleton() {
    bodyEl.innerHTML =
      '<div class="mfg-qv-skeleton" aria-hidden="true">' +
      '<div class="mfg-qv-skeleton__media"></div>' +
      '<div class="mfg-qv-skeleton__line mfg-qv-skeleton__line--lg"></div>' +
      '<div class="mfg-qv-skeleton__line mfg-qv-skeleton__line--md"></div>' +
      '<div class="mfg-qv-skeleton__line mfg-qv-skeleton__line--sm"></div>' +
      '</div>';
  }

  function showError(msg) {
    bodyEl.innerHTML =
      '<p style="text-align:center;padding:40px 16px;color:#888;font-size:14px;">' +
      (msg || 'No se pudo cargar el producto.') +
      '</p>';
  }

  function open(handle) {
    if (!handle) return;
    lastFocused = document.activeElement;
    drawer.removeAttribute('hidden');
    requestAnimationFrame(function () {
      drawer.setAttribute('data-open', '');
      document.body.classList.add('mfg-quickview-open');
    });
    showSkeleton();

    // Cancel previous fetch if user clicked another card
    if (inFlight) inFlight.abort && inFlight.abort();
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    inFlight = controller;
    var opts = { credentials: 'same-origin' };
    if (controller) opts.signal = controller.signal;

    fetch('/products/' + encodeURIComponent(handle) + '.js', opts)
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (product) {
        if (!product) return showError();
        currentProduct = product;
        selectedOptions = {};
        renderProduct(product);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        showError('Error de conexión. Inténtalo de nuevo.');
      });

    document.addEventListener('keydown', onKeyDown);
  }

  function close() {
    drawer.removeAttribute('data-open');
    document.body.classList.remove('mfg-quickview-open');
    setTimeout(function () {
      drawer.setAttribute('hidden', '');
      bodyEl.innerHTML = '';
      currentProduct = null;
      selectedOptions = {};
    }, 300);
    document.removeEventListener('keydown', onKeyDown);
    if (lastFocused && lastFocused.focus) {
      try { lastFocused.focus(); } catch (e) {}
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'Tab') trapFocus(e);
  }

  function trapFocus(e) {
    var focusables = drawer.querySelectorAll(
      'button:not([disabled]), a[href], input:not([type=hidden]):not([disabled]), [tabindex]:not([tabindex^="-"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function renderProduct(product) {
    var node = tpl.content.cloneNode(true);

    // Image (use featured)
    var img = node.querySelector('[data-mfg-qv-image]');
    if (img) {
      var src = product.featured_image || (product.images && product.images[0]);
      if (src) {
        var base = src.split('?')[0];
        img.src = base + '?width=600';
        img.srcset = base + '?width=400 1x, ' + base + '?width=800 2x';
        img.alt = product.title || '';
      }
    }

    // Title + links
    var titleA = node.querySelector('[data-mfg-qv-link]');
    if (titleA) {
      titleA.textContent = product.title || '';
      titleA.href = '/products/' + product.handle;
    }
    var bottomLink = node.querySelector('[data-mfg-qv-link-bottom]');
    if (bottomLink) bottomLink.href = '/products/' + product.handle;

    // Price + savings
    var priceEl = node.querySelector('[data-mfg-qv-price]');
    if (priceEl) {
      var html = '';
      if (product.compare_at_price && product.compare_at_price > product.price) {
        html += '<del>' + formatMoney(product.compare_at_price) + '</del>';
      }
      html += formatMoney(product.price);
      priceEl.innerHTML = html;
    }
    var savingsEl = node.querySelector('[data-mfg-qv-savings]');
    if (savingsEl && product.compare_at_price && product.compare_at_price > product.price) {
      savingsEl.textContent = 'AHORRAS ' + formatMoney(product.compare_at_price - product.price);
      savingsEl.removeAttribute('hidden');
    }

    // Options (color swatches + size chips)
    renderOptions(node, product);

    bodyEl.innerHTML = '';
    bodyEl.appendChild(node);

    bindATC();

    // Auto-select first available variant on init for fast ATC
    autoSelectFirstAvailable();

    // Focus close button for keyboard users
    var closeBtn = drawer.querySelector('.mfg-quickview__close');
    if (closeBtn) {
      setTimeout(function () { closeBtn.focus(); }, 60);
    }
  }

  function renderOptions(node, product) {
    var colorIdx = -1, sizeIdx = -1;
    (product.options || []).forEach(function (name, i) {
      var lower = String(name || '').toLowerCase();
      if (lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1) colorIdx = i;
      if (lower.indexOf('talla') !== -1 || lower.indexOf('size') !== -1) sizeIdx = i;
    });

    if (colorIdx !== -1) renderColorSwatches(node, product, colorIdx);
    if (sizeIdx !== -1) renderSizeChips(node, product, sizeIdx);
  }

  function renderColorSwatches(node, product, idx) {
    var wrap = node.querySelector('[data-mfg-qv-option-color]');
    var container = node.querySelector('[data-mfg-qv-color-swatches]');
    if (!wrap || !container) return;
    var values = uniqueValuesForOption(product, idx);
    if (values.length <= 1) return; // skip single-option

    values.forEach(function (val) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mfg-qv-swatch';
      btn.setAttribute('data-mfg-qv-option-value', val);
      btn.setAttribute('data-mfg-qv-option-idx', idx);
      btn.setAttribute('aria-label', val);
      btn.setAttribute('role', 'radio');

      // Background from variant's featured_image if available
      var variant = product.variants.find(function (v) { return v.options[idx] === val; });
      if (variant && variant.featured_image && variant.featured_image.src) {
        var base = variant.featured_image.src.split('?')[0];
        btn.style.backgroundImage = "url('" + base + "?width=80')";
      } else if (product.featured_image) {
        // Fallback to product hero
        var bb = product.featured_image.split('?')[0];
        btn.style.backgroundImage = "url('" + bb + "?width=80')";
      } else {
        btn.style.background = '#e2e2e2';
      }

      if (!hasAvailability(product, idx, val)) {
        btn.setAttribute('data-unavailable', 'true');
        btn.disabled = true;
      }
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectOption(idx, val);
      });
      container.appendChild(btn);
    });

    wrap.removeAttribute('hidden');
  }

  function renderSizeChips(node, product, idx) {
    var wrap = node.querySelector('[data-mfg-qv-option-size]');
    var container = node.querySelector('[data-mfg-qv-size-chips]');
    if (!wrap || !container) return;
    var values = uniqueValuesForOption(product, idx);
    if (values.length <= 1) return;

    // Dress shirts use compound sizes like "15.5-2" — too complex for
    // quickview's flat chip UI. Surface a link to the full PDP instead.
    var isCompound = values.some(function (s) { return /^\d+(\.\d+)?[\s-]\d+/.test(String(s)); });
    if (isCompound) {
      container.innerHTML =
        '<a href="/products/' + product.handle +
        '" class="mfg-qv__more" style="margin:0;text-align:left;padding:0;">' +
        'Elige cuello y manga en la página completa →</a>';
      wrap.removeAttribute('hidden');
      return;
    }

    values.forEach(function (val) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mfg-qv-chip';
      btn.setAttribute('data-mfg-qv-option-value', val);
      btn.setAttribute('data-mfg-qv-option-idx', idx);
      btn.setAttribute('role', 'radio');
      btn.textContent = val;
      if (!hasAvailability(product, idx, val)) {
        btn.setAttribute('data-unavailable', 'true');
        btn.disabled = true;
      }
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectOption(idx, val);
      });
      container.appendChild(btn);
    });

    wrap.removeAttribute('hidden');
  }

  function uniqueValuesForOption(product, idx) {
    var seen = Object.create(null);
    var out = [];
    (product.variants || []).forEach(function (v) {
      var val = v.options[idx];
      if (val != null && !seen[val]) {
        seen[val] = true;
        out.push(val);
      }
    });
    return out;
  }

  function hasAvailability(product, idx, value) {
    return (product.variants || []).some(function (v) {
      return v.options[idx] === value && v.available;
    });
  }

  function selectOption(idx, value) {
    selectedOptions[idx] = value;

    drawer.querySelectorAll('[data-mfg-qv-option-idx]').forEach(function (el) {
      var elIdx = el.getAttribute('data-mfg-qv-option-idx');
      var elVal = el.getAttribute('data-mfg-qv-option-value');
      if (String(elIdx) === String(idx)) {
        el.setAttribute('data-selected', elVal === value ? 'true' : 'false');
      }
    });

    // Update labels (Color: NAME, Talla: VALUE)
    if (currentProduct && currentProduct.options) {
      currentProduct.options.forEach(function (name, i) {
        if (i !== idx) return;
        var lower = String(name || '').toLowerCase();
        if (lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1) {
          var el = drawer.querySelector('[data-mfg-qv-color-name]');
          if (el) el.textContent = value;
        }
        if (lower.indexOf('talla') !== -1 || lower.indexOf('size') !== -1) {
          var el2 = drawer.querySelector('[data-mfg-qv-size-name]');
          if (el2) el2.textContent = value;
        }
      });
    }

    // If color changed, swap hero image to matching variant featured
    if (currentProduct && currentProduct.options && currentProduct.options[idx]) {
      var lower = String(currentProduct.options[idx]).toLowerCase();
      if (lower.indexOf('color') !== -1 || lower.indexOf('colour') !== -1) {
        swapImageForColor(value, idx);
      }
    }

    updateATC();
  }

  function swapImageForColor(colorValue, idx) {
    if (!currentProduct) return;
    var match = currentProduct.variants.find(function (v) {
      return v.options[idx] === colorValue && v.featured_image;
    });
    var img = drawer.querySelector('[data-mfg-qv-image]');
    if (!img) return;
    var src = (match && match.featured_image && match.featured_image.src) || currentProduct.featured_image;
    if (!src) return;
    var base = src.split('?')[0];
    img.src = base + '?width=600';
    img.srcset = base + '?width=400 1x, ' + base + '?width=800 2x';
  }

  function autoSelectFirstAvailable() {
    if (!currentProduct) return;
    var first = (currentProduct.variants || []).find(function (v) { return v.available; });
    if (!first) return;
    first.options.forEach(function (val, idx) {
      selectOption(idx, val);
    });
  }

  function getMatchingVariant() {
    if (!currentProduct) return null;
    var picks = Object.keys(selectedOptions);
    if (picks.length !== (currentProduct.options || []).length) return null;
    return (currentProduct.variants || []).find(function (v) {
      return v.options.every(function (val, i) {
        return selectedOptions[i] === val;
      });
    });
  }

  function updateATC() {
    var atcBtn = drawer.querySelector('[data-mfg-qv-atc]');
    var label = drawer.querySelector('[data-mfg-qv-atc-label]');
    if (!atcBtn || !label) return;
    var variant = getMatchingVariant();
    if (!variant) {
      atcBtn.disabled = true;
      label.textContent = 'Selecciona opciones';
      delete atcBtn.dataset.variantId;
    } else if (!variant.available) {
      atcBtn.disabled = true;
      label.textContent = 'Agotado';
      delete atcBtn.dataset.variantId;
    } else {
      atcBtn.disabled = false;
      label.textContent = 'AGREGAR AL CARRITO';
      atcBtn.dataset.variantId = variant.id;
    }
  }

  function bindATC() {
    var atcBtn = drawer.querySelector('[data-mfg-qv-atc]');
    if (!atcBtn) return;
    atcBtn.addEventListener('click', handleATC);
  }

  function handleATC() {
    var atcBtn = drawer.querySelector('[data-mfg-qv-atc]');
    var label = drawer.querySelector('[data-mfg-qv-atc-label]');
    if (!atcBtn || !label) return;
    var variantId = atcBtn.dataset.variantId;
    if (!variantId) return;

    var originalLabel = label.textContent;
    atcBtn.disabled = true;
    label.textContent = 'Agregando…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function () {
        label.textContent = '✓ Agregado al carrito';
        atcBtn.setAttribute('data-success', 'true');
        // GA4 event
        try {
          var v = (currentProduct.variants || []).find(function (x) { return String(x.id) === String(variantId); });
          (window.dataLayer = window.dataLayer || []).push({
            event: 'add_to_cart',
            source: 'quickview',
            currency: 'MXN',
            value: v ? v.price / 100 : null,
            items: [{
              item_id: v ? v.sku : null,
              item_name: currentProduct.title,
              item_variant: v ? v.title : null,
              price: v ? v.price / 100 : null,
              quantity: 1
            }]
          });
        } catch (e) { /* ignore */ }

        // Close + surface cart drawer
        setTimeout(function () {
          close();
          document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true }));
        }, 700);
      })
      .catch(function () {
        label.textContent = originalLabel;
        atcBtn.disabled = false;
        atcBtn.removeAttribute('data-success');
      });
  }

  // Global click delegation
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.mfg-quickview-trigger');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      var handle = trigger.dataset.productHandle;
      if (!handle) {
        var href = trigger.getAttribute('href') || '';
        var m = href.match(/\/products\/([^\/?#]+)/);
        handle = m ? m[1] : null;
      }
      if (handle) open(handle);
      return;
    }
    var closer = e.target.closest('[data-mfg-qv-close]');
    if (closer && drawer.contains(closer)) {
      e.preventDefault();
      close();
    }
  }, true);
})();
